import { Link } from "react-router-dom";
import { ArrowRight, Banknote, CalendarCheck, Car, MessageSquare, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import DisclaimerBand from "../components/DisclaimerBand";
import { SITE, IMAGES } from "../config/site";

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page">
      <Seo
        title="How It Works — Empty-Journey Matching Explained"
        description="How Essex Black Cab matches your £100 journey request with licensed Black Cab drivers already travelling between Essex and London. Pay cash or card in the cab."
      />

      <section className="relative overflow-hidden border-b border-[#27272A]">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={IMAGES.driver.url} alt={IMAGES.driver.alt} className="h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">How it works</p>
          <h1 className="mt-4 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Fewer empty cabs. One fixed £100 fare.
          </h1>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight text-white">The empty-journey idea</h2>
          <p className="mt-5 leading-relaxed text-[#A1A1AA]">
            Many TfL-licensed London Black Cab drivers live in Essex. Most working days they
            drive into London to start their shift and drive home to Essex when they finish —
            and those journeys are usually made with an empty cab.
          </p>
          <p className="mt-4 leading-relaxed text-[#A1A1AA]">
            {SITE.name} exists to match passengers with those same journeys. When a driver is
            already travelling your way, you can request a seat in that cab for a single fixed
            fare of £100. The driver earns money on a journey they were making anyway, and you
            travel in a proper, licensed London Black Cab for a fair, pre-agreed price.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: MessageSquare, step: "1", title: "You request", text: "Submit the journey-request form with your direction, addresses, date, time and passenger details. No payment is taken." },
            { icon: CalendarCheck, step: "2", title: "We review & match", text: "Every request is reviewed personally. If it fits the owner's plans they may take it themselves; otherwise it is offered to verified Essex-based Green Badge drivers." },
            { icon: ShieldCheck, step: "3", title: "Journey confirmed", text: "You are told whether a driver can accept. Only then is your booking confirmed — a request on its own is never a confirmed booking." },
            { icon: Banknote, step: "4", title: "You pay £100 in the cab", text: "At the end of the journey you pay your driver £100 directly, by cash or card, inside the Black Cab. The platform never handles your fare." },
          ].map((s) => (
            <div
              key={s.step}
              data-testid={`how-step-${s.step}`}
              className="border border-[#27272A] bg-[#111111] p-7 transition-colors duration-150 hover:border-[rgba(212,175,55,0.4)]"
            >
              <s.icon className="h-7 w-7 text-[#D4AF37]" aria-hidden="true" />
              <p className="mt-4 font-serif text-3xl font-bold text-[#D4AF37]">{s.step}</p>
              <h3 className="mt-2 text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-[#27272A] bg-[#0B0B0B]">
        <div className="section-pad mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-white">Fixed pricing, honestly</h2>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
              <li>• £100 fixed — Essex → London or London → Essex, the same fare either way.</li>
              <li>• No pricing zones, mileage calculations or postcode-based fares.</li>
              <li>• No surge pricing, peak-hour supplements or dynamic pricing.</li>
              <li>• No deposits, advance payments or card details taken online.</li>
              <li>• Drivers decide individually whether a journey is worth accepting — that is how the fare stays fixed.</li>
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-white">Payment, kept simple</h2>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
              <li>• The passenger pays the driver the full £100 inside the cab, by cash or the driver's own card terminal.</li>
              <li>• Every driver is TfL-licensed and verified before joining the network.</li>
              <li>• Your journey is only confirmed once a suitable driver has accepted it.</li>
              <li>• No fare ever passes through this website.</li>
            </ul>
            <div className="mt-8 flex items-start gap-3 border border-[#27272A] bg-[#111111] p-5">
              <Car className="mt-0.5 h-6 w-6 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-[#A1A1AA]">{SITE.paymentMessage}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad mx-auto max-w-4xl px-4 text-center sm:px-6">
        <h2 className="text-3xl font-semibold tracking-tight text-white">Request your journey</h2>
        <p className="mx-auto mt-4 max-w-xl leading-relaxed text-[#A1A1AA]">
          Tell us where and when you need to travel. If a driver is already heading your way,
          your seat is £100 — fixed.
        </p>
        <Link
          to="/book"
          data-testid="how-it-works-cta"
          className="focus-gold mt-8 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
        >
          Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <div className="mx-auto mt-10 max-w-2xl text-left">
          <DisclaimerBand />
        </div>
      </section>
    </main>
  );
}
